import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AutoClearGlSchedule,
  AutoClearResult,
  CreateGlScheduleAccount,
  GlScheduleAccount,
  GlScheduleItemListRow,
  GlScheduleItemStatus,
  GlScheduleSnapshot,
  MatchGlSchedule,
  PartialMatchGlSchedule,
  ReconciliationRow,
  ResetMatchResult,
  SyncGlSchedule,
  SyncGlScheduleResponse,
  UpdateGlScheduleAccount,
  UpdateGlScheduleFollowUp,
  UpdateGlScheduleItemNotes,
  WriteOffGlSchedule,
  WriteOffResult,
} from '@dcc-erp/shared';

import { api } from '../api';

// Cache-key scopes — keyed by companyId per the convention used in
// queries/ar.ts and queries/lva.ts.
const accountsKey = (companyId: string | undefined) => ['gl-schedule-accounts', companyId] as const;
const itemsKey = (companyId: string | undefined) => ['gl-schedule-items', companyId] as const;

export function useGlScheduleAccounts(companyId: string | undefined) {
  return useQuery({
    queryKey: accountsKey(companyId),
    queryFn: () => api<GlScheduleAccount[]>(`/companies/${companyId}/gl-schedule/accounts`),
    enabled: !!companyId,
  });
}

export function useCreateGlScheduleAccount(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateGlScheduleAccount) =>
      api<GlScheduleAccount>(`/companies/${companyId}/gl-schedule/accounts`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountsKey(companyId) });
    },
  });
}

export interface GlScheduleItemFilters {
  accountCode?: string;
  status?: GlScheduleItemStatus;
  asOf?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function useGlScheduleItems(
  companyId: string | undefined,
  filters: GlScheduleItemFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.accountCode) qs.set('accountCode', filters.accountCode);
  if (filters.status) qs.set('status', filters.status);
  if (filters.asOf) qs.set('asOf', filters.asOf);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  const search = qs.toString();
  return useQuery({
    queryKey: [...itemsKey(companyId), filters],
    queryFn: () =>
      api<GlScheduleItemListRow[]>(
        `/companies/${companyId}/gl-schedule/items${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useSyncGlSchedule(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SyncGlSchedule) =>
      api<SyncGlScheduleResponse>(`/companies/${companyId}/gl-schedule/sync`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: itemsKey(companyId) });
    },
  });
}

export function useMatchGlSchedule(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MatchGlSchedule) =>
      api<{ id: string; itemIds: string[] }>(`/companies/${companyId}/gl-schedule/match`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: itemsKey(companyId) });
    },
  });
}

export function useUnmatchGlScheduleItem(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<GlScheduleItemListRow>(`/companies/${companyId}/gl-schedule/items/${id}/unmatch`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: itemsKey(companyId) });
    },
  });
}

export function useUpdateGlScheduleItemNotes(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGlScheduleItemNotes }) =>
      api<GlScheduleItemListRow>(`/companies/${companyId}/gl-schedule/items/${id}/notes`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: itemsKey(companyId) });
    },
  });
}

export function useGlScheduleReconciliation(
  companyId: string | undefined,
  params: { period?: string; asOf?: string } = {},
) {
  const qs = new URLSearchParams();
  if (params.period) qs.set('period', params.period);
  if (params.asOf) qs.set('asOf', params.asOf);
  const search = qs.toString();
  return useQuery({
    queryKey: ['gl-schedule-recon', companyId, params],
    queryFn: () =>
      api<ReconciliationRow[]>(
        `/companies/${companyId}/gl-schedule/reconciliation${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useUpdateGlScheduleAccount(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGlScheduleAccount }) =>
      api<GlScheduleAccount>(`/companies/${companyId}/gl-schedule/accounts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountsKey(companyId) });
    },
  });
}

export function useAutoClearGlSchedule(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AutoClearGlSchedule) =>
      api<AutoClearResult>(`/companies/${companyId}/gl-schedule/auto-clear`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: itemsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['gl-schedule-recon', companyId] });
    },
  });
}

export function useResetGlScheduleMatch(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) =>
      api<ResetMatchResult>(`/companies/${companyId}/gl-schedule/matches/${matchId}/reset`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: itemsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['gl-schedule-recon', companyId] });
    },
  });
}

// M3 — per-item follow-up (assignee / due / status / reason).
export function useUpdateGlScheduleFollowUp(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGlScheduleFollowUp }) =>
      api<GlScheduleItemListRow>(`/companies/${companyId}/gl-schedule/items/${id}/follow-up`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: itemsKey(companyId) });
    },
  });
}

// M3 — write off a within-tolerance residual (posts a JV).
export function useWriteOffGlSchedule(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: WriteOffGlSchedule) =>
      api<WriteOffResult>(`/companies/${companyId}/gl-schedule/write-off`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: itemsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['gl-schedule-recon', companyId] });
    },
  });
}

// M3 — link >=2 open items (partial); items stay OPEN.
export function usePartialMatchGlSchedule(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PartialMatchGlSchedule) =>
      api<{ id: string }>(`/companies/${companyId}/gl-schedule/partial`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: itemsKey(companyId) });
    },
  });
}

// ============================================================
// M4: Period-close snapshots
// ============================================================

const snapshotsKey = (companyId: string | undefined) =>
  ['gl-schedule-snapshots', companyId] as const;

export function useGlScheduleSnapshots(companyId: string | undefined) {
  return useQuery({
    queryKey: snapshotsKey(companyId),
    queryFn: () => api<GlScheduleSnapshot[]>(`/companies/${companyId}/gl-schedule/snapshots`),
    enabled: !!companyId,
  });
}

export function useGlScheduleSnapshot(companyId: string | undefined, period: string) {
  return useQuery({
    queryKey: [...snapshotsKey(companyId), period],
    queryFn: () =>
      api<GlScheduleSnapshot | null>(`/companies/${companyId}/gl-schedule/snapshots/${period}`),
    enabled: !!companyId && !!period,
  });
}

export function useCreateGlScheduleSnapshot(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { period: string }) =>
      api<GlScheduleSnapshot>(`/companies/${companyId}/gl-schedule/snapshots`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: snapshotsKey(companyId) });
    },
  });
}

export function useCertifyGlScheduleSnapshot(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) =>
      api<GlScheduleSnapshot>(`/companies/${companyId}/gl-schedule/snapshots/${period}/certify`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: snapshotsKey(companyId) });
    },
  });
}

export function useLockGlScheduleSnapshot(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) =>
      api<GlScheduleSnapshot>(`/companies/${companyId}/gl-schedule/snapshots/${period}/lock`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: snapshotsKey(companyId) });
    },
  });
}
